import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const VideoContext = createContext();

export const useVideo = () => {
  const context = useContext(VideoContext);
  if (!context) {
    throw new Error('useVideo must be used within a VideoProvider');
  }
  return context;
};

export const VideoProvider = ({ children }) => {
  const [allVideos, setAllVideos] = useState([]); // All available videos
  const [visibleVideos, setVisibleVideos] = useState([]); // Currently visible videos
  const [loading, setLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [likedVideos, setLikedVideos] = useState({});
  const [bookmarkedVideos, setBookmarkedVideos] = useState({});

  // Function to load videos within a specific range
  const loadVisibleVideos = useCallback((currentIndex) => {
    // Increase the buffer to make scrolling smoother
    const startIdx = Math.max(0, currentIndex - 3);
    const endIdx = Math.min(allVideos.length, currentIndex + 4);
    
    const visibleSlice = allVideos.slice(startIdx, endIdx);
    setVisibleVideos(visibleSlice);
  }, [allVideos]);

  useEffect(() => {
    const loadVideos = async () => {
      try {
        // Import the metadata file generated by the download script
        console.log('Attempting to load video metadata...');
        const videoMetadata = await import('../assets/videos/videos_metadata.json');
        console.log('Loaded video metadata:', videoMetadata.default);
        
        if (!videoMetadata.default || videoMetadata.default.length === 0) {
          setLoadError('No videos found in metadata');
          return;
        }
        
        // Add timestamp to prevent caching
        const videosWithTimestamp = videoMetadata.default.map(video => ({
          ...video,
          src: `${video.src}?t=${Date.now()}`
        }));
        
        setAllVideos(videosWithTimestamp);
        
        // Initialize with first batch of videos
        setVisibleVideos(videosWithTimestamp.slice(0, 6));
      } catch (error) {
        console.error('Error loading videos metadata:', error);
        setLoadError(error.message);
        setAllVideos([]);
        setVisibleVideos([]);
      } finally {
        setLoading(false);
      }
    };

    loadVideos();
  }, []);

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const toggleLike = (videoId) => {
    setLikedVideos(prev => {
      const newLikedVideos = { ...prev };
      if (newLikedVideos[videoId]) {
        delete newLikedVideos[videoId];
      } else {
        newLikedVideos[videoId] = true;
      }
      return newLikedVideos;
    });
  };

  const toggleBookmark = (videoId) => {
    setBookmarkedVideos(prev => {
      const newBookmarkedVideos = { ...prev };
      if (newBookmarkedVideos[videoId]) {
        delete newBookmarkedVideos[videoId];
      } else {
        newBookmarkedVideos[videoId] = true;
      }
      return newBookmarkedVideos;
    });
  };

  const value = {
    videos: allVideos,
    visibleVideos,
    loadVisibleVideos,
    totalCount: allVideos.length,
    loading,
    isMuted,
    loadError,
    toggleMute,
    likedVideos,
    bookmarkedVideos,
    toggleLike,
    toggleBookmark
  };

  return (
    <VideoContext.Provider value={value}>
      {children}
    </VideoContext.Provider>
  );
};

export default VideoContext;